import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, InputLabel, FormControl, Select, IconButton,
  Chip, Typography, Box, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import HistoryIcon from '@mui/icons-material/History';

const initialState = {
  itemNumber: '',
  name: '',
  brand: '',
  category: '',
  price: '',
  costPrice: '',
  stock: '',
  description: '',
  dealer: '', // dealer id or name
  favorite: false,
};


const ProductForm = ({ open, onClose, onSave, product, token }) => {
  const [form, setForm] = useState(initialState);
  const [dealers, setDealers] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  useEffect(() => {
    if (product) {
      // If editing, ensure dealer is a string (id) or empty
      let dealerValue = '';
      if (product.dealer) {
        if (typeof product.dealer === 'object' && product.dealer._id) {
          dealerValue = product.dealer._id;
        } else {
          dealerValue = String(product.dealer);
        }
      }
      setForm({
        ...initialState,
        ...product,
        dealer: dealerValue,
        favorite: !!product.favorite,
        itemNumber: product.itemNumber || '',
      });
    } else setForm(initialState);
  }, [product]);

  useEffect(() => {
    // Fetch dealers for dropdown with auth token
    if (!token) return;
    fetch('/api/dealers', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setDealers(Array.isArray(data) ? data : []));
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  onSave({ ...form, price: Number(form.price), costPrice: Number(form.costPrice) || 0, stock: Number(form.stock), favorite: !!form.favorite });
  };

  const handleViewPurchase = async (purchaseCode) => {
    if (!purchaseCode || !token) return;
    
    try {
      const res = await fetch(`/api/stock-purchases?code=${purchaseCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const purchases = await res.json();
        if (purchases.length > 0) {
          setSelectedPurchase(purchases[0]);
          setPurchaseDialogOpen(true);
        }
      }
    } catch (err) {
      console.error('Failed to fetch purchase:', err);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>{product ? 'Edit Product' : 'Add Product'}</span>
          {product?.priceHistory && product.priceHistory.length > 0 && (
            <Button
              size="small"
              startIcon={<HistoryIcon />}
              onClick={() => setHistoryOpen(true)}
              variant="outlined"
            >
              Price History
            </Button>
          )}
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label="Item Number"
              name="itemNumber"
              value={form.itemNumber}
              onChange={handleChange}
              fullWidth
              inputProps={{ maxLength: 32 }}
              sx={{ fontWeight: 600 }}
              placeholder="Enter item number (optional)"
            />
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
            <TextField label="Brand" name="brand" value={form.brand} onChange={handleChange} required fullWidth />
            <TextField label="Category" name="category" value={form.category} onChange={handleChange} required fullWidth />
            <TextField 
              label="Selling Price" 
              name="price" 
              value={form.price} 
              onChange={handleChange} 
              required 
              type="number" 
              inputProps={{ min: 0, step: 0.01 }} 
              fullWidth 
              helperText="Price customers pay"
            />
            <TextField 
              label="Cost Price" 
              name="costPrice" 
              value={form.costPrice} 
              onChange={handleChange} 
              type="number" 
              inputProps={{ min: 0, step: 0.01 }} 
              fullWidth 
              helperText="Price you paid to get this product"
            />
            <TextField label="Stock" name="stock" value={form.stock} onChange={handleChange} required type="number" inputProps={{ min: 0, step: 1 }} fullWidth />
            <TextField label="Description" name="description" value={form.description} onChange={handleChange} multiline rows={2} fullWidth />
            <FormControl fullWidth>
              <InputLabel id="dealer-label">Dealer (optional)</InputLabel>
              <Select
                labelId="dealer-label"
                name="dealer"
                value={form.dealer}
                label="Dealer (optional)"
                onChange={handleChange}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {dealers.map(d => (
                  <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                aria-label={form.favorite ? 'Unmark as favorite' : 'Mark as favorite'}
                onClick={() => setForm(prev => ({ ...prev, favorite: !prev.favorite }))}
                sx={{ color: form.favorite ? 'gold' : 'grey.400', fontSize: 32 }}
              >
                {form.favorite ? <StarIcon fontSize="large" /> : <StarBorderIcon fontSize="large" />}
              </IconButton>
              <span style={{ fontWeight: 600, fontSize: 18 }}>Favorite</span>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">Save</Button>
        </DialogActions>
      </form>
    </Dialog>

    {/* Price History Dialog */}
    <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon />
          <Typography variant="h6">Price Change History</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          <strong>Product:</strong> {product?.name}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {product?.priceHistory && product.priceHistory.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="right"><strong>Cost Price</strong></TableCell>
                  <TableCell align="right"><strong>Selling Price</strong></TableCell>
                  <TableCell><strong>Changed By</strong></TableCell>
                  <TableCell><strong>Source</strong></TableCell>
                  <TableCell><strong>Stock Purchase Code</strong></TableCell>
                  <TableCell><strong>Reason</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {product.priceHistory.slice().reverse().map((history, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      {new Date(history.changedAt).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      ${history.costPrice?.toFixed(2) || '0.00'}
                    </TableCell>
                    <TableCell align="right">
                      ${history.price?.toFixed(2) || '0.00'}
                    </TableCell>
                    <TableCell>{history.changedBy || 'System'}</TableCell>
                    <TableCell>
                      {history.source === 'stock-purchase' && history.purchaseCode ? (
                        <Chip 
                          label={`Stock Purchase`}
                          size="small"
                          color="primary"
                        />
                      ) : (
                        <Chip 
                          label={history.source || 'manual'}
                          size="small"
                          color={history.source === 'stock-purchase' ? 'primary' : 'default'}
                        />
                      )}
                    </TableCell>
                    <TableCell style={{ fontFamily: 'monospace', fontSize: 13 }}>
                      {history.purchaseCode ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleViewPurchase(history.purchaseCode)}
                          sx={{ 
                            fontFamily: 'monospace', 
                            fontSize: 13, 
                            textTransform: 'none',
                            minWidth: 'auto',
                            p: 0.5,
                            color: 'primary.main'
                          }}
                        >
                          {history.purchaseCode}
                        </Button>
                      ) : ''}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {history.reason || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography color="text.secondary" textAlign="center">No price history available</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setHistoryOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>

    {/* Purchase Details Dialog */}
    <Dialog open={purchaseDialogOpen} onClose={() => setPurchaseDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon />
          <Typography variant="h6">Stock Purchase Details</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedPurchase && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <Typography variant="body2"><strong>Invoice:</strong> {selectedPurchase.invoiceNumber || 'N/A'}</Typography>
              <Typography variant="body2"><strong>Dealer:</strong> {selectedPurchase.dealer?.name}</Typography>
              <Typography variant="body2"><strong>Code:</strong> <span style={{fontFamily:'monospace'}}>{selectedPurchase.purchaseCode || selectedPurchase.id || selectedPurchase._id}</span></Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {new Date(selectedPurchase.purchaseDate || selectedPurchase.createdAt).toLocaleDateString()}
              </Typography>
            </Stack>
            {selectedPurchase.notes && (
              <Typography variant="body2" color="text.secondary">
                <strong>Notes:</strong> {selectedPurchase.notes}
              </Typography>
            )}
            <Divider />
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedPurchase.items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.product?.name || 'Unknown'}</TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="right">${item.unitPrice?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="right">${(item.quantity * item.unitPrice)?.toFixed(2) || '0.00'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} align="right"><strong>Total:</strong></TableCell>
                    <TableCell align="right"><strong>${selectedPurchase.total?.toFixed(2) || '0.00'}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPurchaseDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default ProductForm;
